import React from "react";
import { motion } from "framer-motion";
import { PROGRAMS } from "../data/content";
import { ArrowUpRight } from "lucide-react";

const accentColor = {
  cornflower: "#648DE5",
  lime: "#C6D947",
  teal: "#5BC4B8",
  turquoise: "#3AB0D6",
};

export default function Programs() {
  return (
    <section
      id="programs"
      className="relative py-14 md:py-28 bg-[#E8F1DF] overflow-hidden"
      data-testid="programs-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 md:gap-6 mb-10 md:mb-16">
          <div className="max-w-2xl">
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-5 block">
              Programs by Age
            </span>
            <h2 className="font-display text-[#0F1B47] text-[32px] md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-semibold">
              From first steps to
              <br />
              <span className="italic font-normal">big-school ready</span>.
            </h2>
          </div>
          <p className="text-[#4A5568] text-base leading-relaxed font-light max-w-md">
            Four gently rising programs — each with its own tempo, its own wonder. All
            anchored by the Cambridge Early Years approach and our unhurried way of
            teaching.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-5">
          {PROGRAMS.map((p, i) => (
            <motion.a
              key={p.id}
              href="#admissions"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bento-card group relative bg-[#FAFAF5] rounded-3xl overflow-hidden border border-[#0F1B47]/6"
              data-testid={`program-card-${p.id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img decoding="async" loading="lazy"
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F1B47]/70 via-[#0F1B47]/10 to-transparent" />
                <div className="absolute top-5 left-5">
                  <span
                    className="inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-3 py-1.5 rounded-full text-[#0F1B47]"
                    style={{ background: accentColor[p.accent] }}
                  >
                    {p.age}
                  </span>
                </div>
                <div className="absolute top-5 right-5 w-10 h-10 rounded-full bg-[#FDFDF9] flex items-center justify-center opacity-0 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
                  <ArrowUpRight size={18} className="text-[#0F1B47]" />
                </div>
              </div>
              <div className="p-5 md:p-6">
                <h3 className="font-display text-xl md:text-2xl text-[#0F1B47] font-medium mb-1">
                  {p.name}
                </h3>
                <p className="text-[12px] md:text-[13px] font-semibold tracking-wide text-[#648DE5] mb-2 md:mb-3">
                  {p.tagline}
                </p>
                <p className="text-[#4A5568] text-[13.5px] md:text-sm leading-relaxed font-light">
                  {p.blurb}
                </p>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
